'use client';

import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Trans } from '@kit/ui/trans';

import {
  PHOTO_CATEGORIES,
  VIDEO_CATEGORIES,
  type PhotoCategory,
  type VideoCategory,
} from '../lib/media-categories';

interface Props {
  /** Determines which option set is shown. Photo / video have disjoint
   * lists (except `audition`, which lives in both). Document / headshot
   * media types do not get a category picker — pass photo only when
   * editing photo rows, video for video rows. */
  mediaType: 'photo' | 'video';
  /** Current value. NULL = uncategorised. */
  value: string | null;
  onValueChange: (value: string | null) => void;
  /** Disable while a save is in flight. */
  disabled?: boolean;
  /** Accessible label override. Defaults to the localized "Category". */
  ariaLabel?: string;
  /** Custom placeholder. Defaults to the localized "Uncategorised". */
  placeholder?: string;
}

const UNCATEGORISED = '__none__';

/**
 * Single-select picker for the editorial `category` field on
 * `artist_media`. Used in three editor surfaces:
 *   - Dancer setup wizard (categorize step)
 *   - Admin media tab (edit dialog)
 *   - Future: client-user media tab if upload is enabled
 *
 * Backed by the `artist_media_category_check` DB CHECK constraint.
 * Legacy Meteor values (e.g. `ballet_class`) are NOT exposed in the
 * options — they stay in the DB but render as "Other" in the public
 * profile until a dancer manually re-categorises them through this
 * picker.
 */
export function MediaCategoryPicker({
  mediaType,
  value,
  onValueChange,
  disabled,
  ariaLabel,
  placeholder,
}: Props) {
  const { t } = useTranslation();
  const resolvedAriaLabel = ariaLabel ?? t('dancer:mediaCategoryPicker.label');
  const resolvedPlaceholder =
    placeholder ?? t('dancer:mediaCategoryPicker.uncategorised');
  const options = mediaType === 'photo' ? PHOTO_CATEGORIES : VIDEO_CATEGORIES;
  const isInOptions = options.some((o) => o.value === value);
  const selectValue = isInOptions ? (value ?? UNCATEGORISED) : UNCATEGORISED;
  const showLegacyHint = value !== null && !isInOptions;

  return (
    <div className="space-y-1.5">
      <Select
        value={selectValue}
        onValueChange={(next) =>
          onValueChange(
            next === UNCATEGORISED
              ? null
              : (next as PhotoCategory | VideoCategory),
          )
        }
        disabled={disabled}
      >
        <SelectTrigger aria-label={resolvedAriaLabel} className="w-full">
          <SelectValue placeholder={resolvedPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={UNCATEGORISED}>{resolvedPlaceholder}</SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              <div className="flex flex-col">
                <span>{opt.label}</span>
                <span className="text-muted-foreground text-xs">
                  {opt.description}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showLegacyHint && (
        <p className="text-muted-foreground text-xs">
          <Trans
            i18nKey="dancer:mediaCategoryPicker.legacyHint"
            values={{ value }}
            components={{ code: <code /> }}
          />
        </p>
      )}
    </div>
  );
}
